import React, { useEffect, useState } from "react";
import { FaUsers } from "react-icons/fa";
import { FaTrashAlt } from "react-icons/fa";
import { useDeleteResourceActions } from "utils/store";
import { FaClone } from "react-icons/fa";

import { Link, useNavigate } from "react-router-dom";
import { CourseApi, CourseData, coursesApi } from "api";
import { useCourses } from "api/hooks";
import { PaziaButton } from "components/atoms";
import {
  TanTable,
  TanTableColumn,
  useFilters,
  useInitFilters,
  usePagination,
  useSorting,
  AdminResourceDeleteModal,
  CourseBadgeStatus,
  PageMain,
} from "components/molecules";
import { routes } from "consts";
import { formatDate } from "utils";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { DuplicateCourseModal } from "components/templates";
import { CategoriesChips } from "components/molecules/CategoriesChips";
import { CourseFlags } from "./CourseFlags";
import { COURSE_STATUS_OPTIONS, CourseStatus } from "consts/courseType";

const FILTER_NAME = "courses-filter";
export const CoursesPage = () => {
  const { getInitFilterValue, getInitFilterValues } = useInitFilters(FILTER_NAME);

  const categoryIds = getInitFilterValues("category");

  return (
    <CoursesPageInner
      initValues={{
        category: categoryIds[0] ?? -1,
        categoryIds,
        name: getInitFilterValue("name", "like") ?? "",
        id: getInitFilterValue("id", "like"),
        status: getInitFilterValues("status"),
        places: getInitFilterValues("places"),
      }}
    />
  );
};

interface CoursesPageInnerProps {
  initValues: {
    category: number;
    categoryIds: number[];
    name: string;
    id?: string;
    status: number[];
    places: number[];
  };
}
// TODO Nie działa zapamietywanie filtra ID
const CoursesPageInner = ({ initValues }: CoursesPageInnerProps) => {
  const navigate = useNavigate();
  const [duplicateCourseId, setDuplicateCourseId] = useState<number | null>(null);

  const { filter, changeFilter, updateAllFilters } = useFilters(FILTER_NAME);
  const [selectedCategory, setSelectedCategory] = useState<number>(initValues.category);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(initValues.categoryIds);
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting("dateOfCourse", "ASC");
  const { data, isLoading, total, refetch, isFetching } = useCourses(skip, limit, undefined, filter, fieldWithOrder);

  const onSuccessDelete = () => refetch();

  const onSuccessDuplicate = (course: CourseApi) => {
    navigate(routes.COURSES_MANAGE(course.data.id));
    refetch();
  };

  useEffect(() => {
    refetch();
  }, [sorting, pagination, filter]);

  const columns = React.useMemo<TanTableColumn<CourseData>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
        size: 10,
        enableColumnFilter: true,
        meta: {
          filterVariant: "id",
        },
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Nazwa",
        enableColumnFilter: true,
        size: 400,
        cell: (info) => (
          <span className="flex flex-col">
            <span>{info.row.original.name}</span>
            {info.row.original.variant && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{info.row.original.variant}</span>
            )}
          </span>
        ),
      },
      {
        id: "category",
        accessorKey: "dateOfCourse",
        header: "Data",
        size: 150,
        enableColumnFilter: false,
        cell: (info) => (
          <span>
            {formatDate(info.row.original.dateOfCourse, false, false, false)} -{" "}
            {formatDate(info.row.original.endOfCourse)}
          </span>
        ),
      },
      {
        id: "numberOfStudents",
        accessorKey: "numberOfStudents",
        header: "Kursanci",
        size: 90,
        enableColumnFilter: false,
        cell: (info) => {
          const count = info.getValue<number>();
          return (
            <Link
              to={routes.COURSES_MANAGE_NESTED.STUDENTS(info.row.original.id)}
              onClick={(e) => e.stopPropagation()}
              className="text-md text-center font-semibold hover:underline"
            >
              {count}
            </Link>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        enableColumnFilter: true,
        header: "Status",
        size: 150,
        cell: (info) => <CourseBadgeStatus status={info.getValue<CourseStatus>()} />,
        meta: {
          filterVariant: "select",
          options: COURSE_STATUS_OPTIONS,
        },
      },
      {
        id: "publicDataReady",
        accessorKey: "publicDataReady",
        header: "Stan",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => <CourseFlags {...info.row.original} courseId={info.row.original.id} />,
      },
      {
        id: "actions",
        accessorKey: "id",
        header: "Akcje",
        size: 90,
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => <CoursesTableActions courseId={info.getValue<number>()} onDuplicate={setDuplicateCourseId} />,
      },
    ],
    [],
  );

  return (
    <>
      <PageMain>
        <PageRichHeader header="Kursy">
          <div className="flex grow justify-end sm:justify-between">
            <div>
              <PaziaButton type="primary" title="Dodaj +" onClick={() => navigate(routes.COURSES_ADD)} />
            </div>
          </div>
        </PageRichHeader>
        <div className="flex flex-col gap-4 md:flex-row">
          <div className="md:shrink-0">
            <CategoriesChips
              onSelect={(id, allIds) => {
                changeFilter("category", id === -1 ? "" : `category in (${allIds.join(",")})`);
                setSelectedCategory(id);
                setSelectedCategoryIds(allIds);
              }}
              selected={selectedCategory}
              pinStorageKey="courses-pinned-categories"
            />
          </div>

          <div className="min-w-0 flex-1">
            <TanTable
              data={data}
              columns={columns}
              total={total}
              loading={isLoading}
              fetching={isFetching}
              initialFilters={[
                {
                  id: "name",
                  value: initValues.name,
                },
                {
                  id: "id",
                  value: initValues.id,
                },
                {
                  id: "status",
                  value: initValues.status,
                },
                {
                  id: "places",
                  value: initValues.places,
                },
              ]}
              onPaginationChange={onPaginationChange}
              onSortingChange={onSortingChange}
              onClearFilters={() => {
                setSelectedCategory(-1);
                setSelectedCategoryIds([]);
                updateAllFilters([]);
              }}
              pagination={pagination}
              onFiltersChange={(filters) => {
                const catFilter =
                  selectedCategory === -1
                    ? { id: "category", value: "", apiFilter: "" }
                    : {
                        id: "category",
                        value: selectedCategoryIds.join(","),
                        apiFilter: `category in (${selectedCategoryIds.join(",")})`,
                      };
                updateAllFilters([...filters, catFilter].map((x) => ({ key: x.id, value: x.apiFilter })));
              }}
              sorting={sorting}
              onRowClick={(x, row) => navigate(routes.COURSES_MANAGE(x.id))}
            />
          </div>
        </div>
      </PageMain>
      <AdminResourceDeleteModal
        title="Usuń edycję kursu"
        description="Jesteś pewien że chcesz usunąć edycję kursu?"
        onSuccessDelete={onSuccessDelete}
        deleteAction={coursesApi.delete}
      />

      <DuplicateCourseModal
        courseId={duplicateCourseId}
        onCloseRequest={() => {
          setDuplicateCourseId(null);
        }}
        onSuccessDuplicate={onSuccessDuplicate}
      />
    </>
  );
};

export const CoursesTableActions = ({
  courseId,
  onDuplicate,
}: {
  courseId: number;
  onDuplicate: (cId: number) => void;
}) => {
  const navigate = useNavigate();
  const { setDeleteResource } = useDeleteResourceActions();
  return (
    <div className="flex gap-2">
      <FaTrashAlt
        size={20}
        className="cursor-pointer transition-colors duration-200 ease-in-out hover:text-rose-600 dark:hover:text-white"
        onClick={(e) => {
          e.stopPropagation();
          setDeleteResource(courseId);
        }}
      />
      <FaClone
        size={20}
        className="hover:text-primary-600 cursor-pointer transition-colors duration-200 ease-in-out dark:hover:text-white"
        onClick={(e) => {
          e.stopPropagation();
          onDuplicate(courseId);
        }}
      />
      <FaUsers
        size={20}
        className="hover:text-primary-600 cursor-pointer transition-colors duration-200 ease-in-out dark:hover:text-white"
        onClick={(e) => {
          e.stopPropagation();
          navigate(routes.COURSES_MANAGE_NESTED.STUDENTS(courseId));
        }}
      />
    </div>
  );
};
